import React, { useMemo } from "react";
import { connect } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { Formik, Form, FieldArray } from "formik";
import { format, isValid } from "date-fns";
import uniqueId from "lodash/uniqueId";
import get from "lodash/get";
import * as Yup from "yup";
import {
  AutoSaveField as Field,
  SelectAutocomplete,
  Checkbox,
  Input,
  DatePicker
} from "../../../../components/Form";
import { getRequiredMessage, getInvalidMessage } from "../../../../utils/getValidationMessage";
import { createAddCitizenshipHandler, isAdditionalCitizenshipDisabled } from "./utils";
import { getSignatories } from "../../../../store/selectors/appConfig";
import { updateProspect } from "../../../../store/actions/appConfig";
import { ALPHANUMERIC_REGEX } from "../../../../utils/validation";
import { MAX_PASSPORT_NUMBER_LENGTH } from "./constants";
import { NationalityCheckbox } from "./NationalityCheckbox";
import { SubmitButton } from "./../SubmitButton/SubmitButton";
import { DATE_FORMAT } from "../../../../constants";
import { useStyles } from "./styled";

const MAX_ANOTHER_CITIZENSHIP = 4;

const nationalitySchema = () =>
  Yup.object().shape({
    passportDetails: Yup.array().of(
      Yup.object().shape({
        country: Yup.string().required(getRequiredMessage("Nationality")),
        countryOfIssuance: Yup.string().required(getRequiredMessage("Country of issuance")),
        passportNumber: Yup.string()
          .required(getRequiredMessage("Passport number"))
          .max(12, "Maximum 12 characters allowed")
          .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Passport number")),
        passportIssueDate: Yup.date()
          .nullable()
          .when("passportNumber", {
            is: passportNumber => !!passportNumber,
            then: Yup.date()
              .required(getRequiredMessage("Passport Issue Date"))
              .test(
                "not-older-than-10-years",
                "Passport Issue Date should not be older than 10 years from today",
                function(issueDate) {
                  if (!issueDate) return true;
                  const tenYearsAgo = new Date();
                  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
                  return issueDate >= tenYearsAgo;
                }
              )
              .test("not-in-future", getInvalidMessage("Passport Issue Date"), function(issueDate) {
                if (!issueDate) return true;
                return issueDate <= new Date();
              })
              .typeError(getInvalidMessage("Passport Issue Date")),
            otherwise: Yup.date().nullable()
          }),
        passportExpiryDate: Yup.date()
          .nullable()
          .required(getRequiredMessage("Passport Expiry Date"))
          .test(
            "issue-date-filled",
            "Passport Issue Date must be filled before selecting Passport Expiry Date",
            function(expiryDate) {
              const { passportIssueDate } = this.parent;
              return !!passportIssueDate || !expiryDate;
            }
          )
          .test(
            "not-greater-than-10-years-from-issue",
            "Passport Expiry Date should not be more than 10 years after Passport Issue Date",
            function(expiryDate) {
              const { passportIssueDate } = this.parent;
              if (!expiryDate || !passportIssueDate) return true;
              const issueDate = new Date(passportIssueDate);
              const maxExpiryDate = new Date(issueDate);
              maxExpiryDate.setFullYear(maxExpiryDate.getFullYear() + 10);
              return expiryDate <= maxExpiryDate;
            }
          )
          .test(
            "greater-than-issue-date",
            "Passport Expiry Date must be greater than Passport Issue Date",
            function(expiryDate) {
              const { passportIssueDate } = this.parent;
              if (!expiryDate || !passportIssueDate) return true;
              return new Date(expiryDate) > new Date(passportIssueDate);
            }
          )
          .test("future-date", "Passport Expiry Date must be a future date", function(expiryDate) {
            if (!expiryDate) return true;
            return new Date(expiryDate) > new Date();
          })
          .typeError(getInvalidMessage("Passport Expiry Date"))
      })
    )
  });

const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const NationalityStep = ({
  index,
  passportDetails,
  handleContinue,
  createFormChangeHandler
}) => {
  const classes = useStyles();

  const kycDetailsPath = `prospect.signatoryInfo[${index}].kycDetails`;
  const initialValues = useMemo(
    () => ({
      passportDetails: passportDetails.map(item => ({
        ...item,
        id: uniqueId(),
        passportIssueDate: item.passportIssueDate || "",
        countryOfIssuance: item.countryOfIssuance || ""
      }))
    }),
    [passportDetails]
  );

  const dateFormatHandler = value => {
    if (!value || !isValid(value) || typeof value === "string") return value;
    return format(value, DATE_FORMAT);
  };

  return (
    <Formik
      onSubmit={handleContinue}
      initialValues={initialValues}
      validationSchema={nationalitySchema()}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, errors }) => (
        <Form>
          <FieldArray
            name="passportDetails"
            render={arrayHelper =>
              values.passportDetails.map((item, passportIndex) => {
                // eslint-disable-next-line max-len
                const passportDetailsPath = `${kycDetailsPath}.passportDetails[${passportIndex}]`;
                return (
                  <Grid container spacing={3} key={item.id}>
                    {!!passportIndex && <Grid item xs={12} className={classes.divider} />}
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].country`}
                        path={`${passportDetailsPath}.country`}
                        label="Nationality"
                        component={SelectAutocomplete}
                        datalistId="nationality"
                        filterOptions={options => {
                          const nationalities = values.passportDetails
                            .filter((item, index) => item.country && index !== passportIndex)
                            .map(item => item.country);

                          return options.filter(item => !nationalities.includes(item.value));
                        }}
                        changeProspect={(prospect, value) => {
                          if (passportIndex) {
                            return {
                              ...prospect,
                              [`${kycDetailsPath}.dualCitizenshipCountry`]: values.passportDetails
                                .map(p => p.country)
                                .slice(1)
                            };
                          }
                          return {
                            ...prospect,
                            [`${kycDetailsPath}.nationality`]: value
                          };
                        }}
                        shrink={true}
                        tabIndex={2 * passportIndex + 1}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].passportNumber`}
                        path={`${passportDetailsPath}.passportNumber`}
                        label="Passport number"
                        component={Input}
                        contextualHelpText="If Passport Number contains hyphen (-), oblique (/), spaces or any other special character please enter only alphabets and numbers.
                            Example: 'P-123/1950/456 to be entered as P1231950456'"
                        InputProps={{
                          inputProps: {
                            maxLength: MAX_PASSPORT_NUMBER_LENGTH,
                            tabIndex: 0
                          }
                        }}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].passportIssueDate`}
                        path={`${passportDetailsPath}.passportIssueDate`}
                        label="Passport Issue Date"
                        component={DatePicker}
                        InputProps={{
                          inputProps: { tabIndex: 0 }
                        }}
                        changeProspect={changeDateProspectHandler}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].passportExpiryDate`}
                        path={`${passportDetailsPath}.passportExpiryDate`}
                        label="Passport Expiry Date"
                        component={DatePicker}
                        InputProps={{
                          disableUnderline: true,
                          inputProps: {
                            tabIndex: 0,
                            "data-testid": "passportExpiryDate"
                          }
                        }}
                        minDate={new Date()}
                        maxDate={new Date(new Date().getFullYear() + 10, 11, 31)}
                        disableFuture={false}
                        changeProspect={changeDateProspectHandler}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].countryOfIssuance`}
                        path={`${passportDetailsPath}.countryOfIssuance`}
                        label="Country of issuance"
                        placeholder="Select country of issuance"
                        component={SelectAutocomplete}
                        datalistId="nationality"
                        shrink={true}
                        tabIndex={2 * passportIndex + 1}
                        InputProps={{
                          inputProps: {
                            id: `countryOfIssuance-${passportIndex}`
                          }
                        }}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} className={classes.anotherCitizenshipContainer}>
                      {passportIndex < MAX_ANOTHER_CITIZENSHIP && (
                        <Field
                          name={`passportDetails[${passportIndex}].hasAnotherCitizenship`}
                          path={`${passportDetailsPath}.hasAnotherCitizenship`}
                          label="This person has another citizenship"
                          component={NationalityCheckbox}
                          onChange={createAddCitizenshipHandler(arrayHelper, passportIndex)}
                          changeProspect={prospect => ({
                            ...prospect,
                            [`${kycDetailsPath}.passportDetails`]: values.passportDetails.map(
                              ({ id, passportIssueDate, passportExpiryDate, ...withoutId }) => {
                                return {
                                  ...withoutId,
                                  passportIssueDate: dateFormatHandler(passportIssueDate),
                                  passportExpiryDate: dateFormatHandler(passportExpiryDate)
                                };
                              }
                            ),
                            [`${kycDetailsPath}.dualCitizenshipCountry`]: values.passportDetails
                              .map(p => p.country)
                              .slice(1),
                            [`${kycDetailsPath}.dualCitizenship`]: !!passportIndex
                          })}
                          disabled={isAdditionalCitizenshipDisabled(values, passportIndex, errors)}
                          inputProps={{ tabIndex: 2 * passportIndex + 3 }}
                        />
                      )}
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <Field
                        name={`passportDetails[${passportIndex}].diplomatPassport`}
                        path={`${passportDetailsPath}.diplomatPassport`}
                        label="This is a diplomatic passport"
                        component={Checkbox}
                        inputProps={{ tabIndex: 2 * passportIndex + 4 }}
                      />
                    </Grid>
                  </Grid>
                );
              })
            }
          />
          <SubmitButton tabIndex={values.passportDetails.length * 2} />
        </Form>
      ))}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => ({
  passportDetails: get(getSignatories(state)[index], "kycDetails.passportDetails", [])
});

const mapDispatchToProps = {
  updateProspect
};

export const Nationality = connect(
  mapStateToProps,
  mapDispatchToProps
)(NationalityStep);
